import type {
  FormattedMemberPromotion,
  FormattedMemberPromotionsResponse,
  FormattedPromotionUserDetails,
  MemberPromotion,
  MemberPromotionAPIResponse,
  MemberPromotionUser,
  MemberPromotionsAPIResponse,
  MemberPromotionsIncluded,
  MemberPromotionsIncludes
} from '@library/types/members/memberPromotions';
import {
  getDateTime,
  getFormattedDateTime
} from '@bo-utils/members/memberCommon';
import { type ComboboxOption } from '@library/components/selects';
import { FETCH_PROMOTIONS_PER_PAGE_COUNT } from '@bo-constants/member/members';
import type { GenericFilterTypesApiResponse } from '@library/types/utils';
import { values } from 'ramda';

export const getMemberPromotion = ({
  included,
  memberPromotion
}: {
  included: MemberPromotionsIncludes;
  memberPromotion: MemberPromotion;
}): FormattedMemberPromotion => {
  // Ensure included is always an array, even if undefined or null from API
  const safeIncluded: MemberPromotionsIncludes = Array.isArray(included)
    ? included
    : [];

  const {
    attributes: {
      fund_type: fundType,
      long_description: description,
      name,
      opt_in: optIn,
      start_time: startDateValue,
      type: promotionType,
      use_by: endDateValue,
      user_new_wager_ids: memberPromoWagerIds = []
    },
    relationships: {
      community_play: communityPlay,
      promotion_users: promotionUsersRelationship
    },
    id: promotionId
  } = memberPromotion;

  const promotionUserRefs = promotionUsersRelationship.data || [];
  const promotionUsersDetails: Array<FormattedPromotionUserDetails> =
    promotionUserRefs.map((userRef: MemberPromotionUser) => {
      const promotionUserFull = safeIncluded.find(
        (item: MemberPromotionsIncluded) =>
          item.type === 'promotion_user' && item.id === userRef.id
      );

      if (!promotionUserFull || !promotionUserFull.attributes) {
        return {
          id: userRef.id,
          optedInAtDate: '',
          optedInAtTime: '',
          redeemedAtDate: '',
          redeemedAtTime: ''
        };
      }

      const { formattedDate: redeemedAtDate, formattedTime: redeemedAtTime } =
        getFormattedDateTime({
          dateTime: getDateTime({
            dateString: promotionUserFull.attributes.redeemed_at || ''
          })
        });

      const { formattedDate: optedInAtDate, formattedTime: optedInAtTime } =
        getFormattedDateTime({
          dateTime: getDateTime({
            dateString: promotionUserFull.attributes.opted_in_at || ''
          })
        });

      return {
        id: userRef.id,
        optedInAtDate,
        optedInAtTime,
        redeemedAtDate,
        redeemedAtTime
      };
    });

  const { formattedDate: startDate, formattedTime: startTime } =
    getFormattedDateTime({
      dateTime: getDateTime({ dateString: startDateValue || '' })
    });

  const { formattedDate: endDate, formattedTime: endTime } =
    getFormattedDateTime({
      dateTime: getDateTime({ dateString: endDateValue || '' })
    });

  return {
    communityPlayId: communityPlay.data?.id || '',
    description,
    endDate,
    endTime,
    fundType,
    memberPromoWagerIds,
    name,
    optIn,
    promotionId,
    promotionType,
    promotionUsersDetails,
    startDate,
    startTime,
    // Populate top-level fields for when there's a single promotion user row or as default (no promotion users)
    optedInAtDate: promotionUsersDetails[0]?.optedInAtDate || '',
    optedInAtTime: promotionUsersDetails[0]?.optedInAtTime || '',
    redeemedAtDate: promotionUsersDetails[0]?.redeemedAtDate || '',
    redeemedAtTime: promotionUsersDetails[0]?.redeemedAtTime || ''
  };
};

export const transformMemberPromotions = ({
  response,
  page,
  perPage
}: {
  response: MemberPromotionsAPIResponse;
  page: number;
  perPage: number;
}): FormattedMemberPromotionsResponse => {
  const { data, included } = response;

  const memberPromotions = data.map((memberPromotion) =>
    getMemberPromotion({ included, memberPromotion })
  );

  return {
    memberPromotions,
    totalPages:
      memberPromotions.length < (perPage || FETCH_PROMOTIONS_PER_PAGE_COUNT)
        ? page
        : page + 1
  };
};

export const transformMemberPromotion = ({
  response
}: {
  response: MemberPromotionAPIResponse;
}): FormattedMemberPromotion => {
  const { data: memberPromotion, included } = response;
  return getMemberPromotion({ included, memberPromotion });
};

const formatPromotionType = ({
  promotionType
}: {
  promotionType: string | undefined;
}) => (promotionType ? promotionType.replace(/([A-Z])/g, ' $1').trim() : '');

export const transformPromotionTypes = ({
  response
}: {
  response: GenericFilterTypesApiResponse | undefined;
}): Array<ComboboxOption> => {
  if (!response) {
    return [];
  }

  const promotionTypeOptions: Array<ComboboxOption> = values(response)
    .sort()
    .map((promotionType) => ({
      label: formatPromotionType({ promotionType }),
      value: promotionType
    }));

  return promotionTypeOptions;
};
